import { Button, Flex } from "@theorgchartdev/storybook";
import SectionCard, {
  SectionCardControls,
  SectionCardGroup
} from "../../../../Composites/SectionCard/SectionCard";
import { GetConnectionConfigured } from "../Utils";
import {
  GetFieldMap,
  GetAvailableFields,
  GetCostPositionEmployeeFieldGroups,
  MAPPING_TEST_PREFIX
} from "./Utils";
import * as StateLib from "@/Utils/StateLib";
import * as React from "react";
import { appstr } from "@coreapp/utils/appstr";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { SynthesizePositions } from "@coreapp/utils/SynthesizePositions";
import { enumPositionDataIngestMode } from "@coreapp/constants";
import { PositionHolderFields } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/PositionHolderFields";
import { AdditionalReportsMappingSection } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/AdditionalReportsMappingSection";
import { EmployeeMappingSection } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/EmployeeMappingSection";
import { PositionMappingSection } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/PositionMappingSection";
import { BudgetMappingSection } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/BudgetMappingSection";
import { OptionalFieldMappingSection } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/OptionalFieldMappingSection";
import { LocalizationCard } from "./LocalizationCard";

export const MappingConfiguration = ({
  chartConfig,
  handleUpdate,
  handleLoadFieldMapping,
  handleResetFieldMapping
}) => {
  if (!chartConfig) return;

  let wsMgr = StateLib.GetWSMgr();

  const eventTracker = wsMgr.GetUsageTracker();
  React.useEffect(() => {
    eventTracker.ReportEvent("chart_settings.mapping.opened");
  }, []);

  const localConfig = { ...chartConfig, fields: GetFieldMap(chartConfig) };
  const disableDropdowns = localConfig.headers == null || localConfig.headers.length == 0;
  const isPositionChart = IsPositionChart(chartConfig);
  const synthesizePositions = SynthesizePositions(chartConfig);
  const isSingleCombinedOrganicPositionChart =
    isPositionChart &&
    chartConfig.positionChart?.dataIngestionMode === enumPositionDataIngestMode.multiSingle;
  const showPositionHolderFields = synthesizePositions || isSingleCombinedOrganicPositionChart;

  const handleFieldMappingChange = (option, key) => {
    let nextFieldMap = structuredClone(localConfig["fields"]);
    nextFieldMap[key] = option.value;
    handleUpdate({ fields: nextFieldMap }, "fields");
  };

  const handleFieldRename = (fieldId, newFieldName) => {
    let nextFieldMap = structuredClone(localConfig["fields"]);
    if (newFieldName === "") {
      newFieldName = null;
    }
    if (fieldId in nextFieldMap) {
      nextFieldMap[fieldId] = newFieldName;
    }
    handleUpdate({ fields: nextFieldMap }, "fields");
  };

  const handleCustomFieldNameValidation = (fieldId, newName) => {
    const requiredFields = ["PersonID", "SupervisorID", "Name", "Title"];
    const fieldMap = localConfig["fields"];

    const costFieldGroups = GetCostPositionEmployeeFieldGroups();
    const budgetFieldIds = [];

    if (costFieldGroups) {
      if (costFieldGroups.posFieldGroup) {
        budgetFieldIds.push(...Object.keys(costFieldGroups.posFieldGroup));
      }
      if (costFieldGroups.empFieldGroup) {
        budgetFieldIds.push(...Object.keys(costFieldGroups.empFieldGroup));
      }
      if (costFieldGroups.costBandGroup) {
        budgetFieldIds.push(...Object.keys(costFieldGroups.costBandGroup));
      }
    }

    const uniqueMappingFields = [...requiredFields, ...budgetFieldIds];

    let requiredValues = [];
    if (fieldMap) {
      if (uniqueMappingFields.indexOf(fieldId) > -1) {
        // get lowercase list of req mapped values in use to check against
        Object.keys(fieldMap).forEach(function (item) {
          if (uniqueMappingFields.indexOf(item) > -1 && fieldMap[item] !== null)
            requiredValues.push(fieldMap[item]);
        });
      }
    }
    const nameAllowed = requiredValues.indexOf(newName) === -1;
    return { success: nameAllowed, errorResponse: appstr("nameinuse") };
  };

  const BuildFieldMappingOptions = fieldMapID => {
    let options = [];
    let headers = GetAvailableFields(localConfig, fieldMapID);

    for (let header of headers) {
      let option = {
        value: header,
        label: header,
        clickHandler: e => handleFieldMappingChange(e, fieldMapID)
      };
      options.push(option);
    }
    return options;
  };

  return (
    <SectionCardGroup>
      <SectionCardControls>
        <Button
          dataTestId={"map-config-load-btn"}
          className={"oc-special-secondary"}
          modifier={"secondary"}
          iconProps={{ name: "arrows-rotate" }}
          onClick={handleLoadFieldMapping}
          disabled={!localConfig || !GetConnectionConfigured(localConfig)}
        >
          {appstr("LoadMap")}
        </Button>
        <Flex className={"oc-right-justify"}>
          <Button
            dataTestId={`${MAPPING_TEST_PREFIX}-reset-btn`}
            modifier={"secondary"}
            onClick={handleResetFieldMapping}
          >
            {appstr("ResetMap")}
          </Button>
        </Flex>
      </SectionCardControls>
      {isPositionChart ? (
        <PositionMappingSection
          localConfig={localConfig}
          BuildFieldMappingOptions={BuildFieldMappingOptions}
          disableDropdowns={disableDropdowns}
          handleFieldRename={handleFieldRename}
          handleCustomFieldNameValidation={handleCustomFieldNameValidation}
          onUpdate={handleUpdate}
        />
      ) : (
        <EmployeeMappingSection
          localConfig={localConfig}
          BuildFieldMappingOptions={BuildFieldMappingOptions}
          disableDropdowns={disableDropdowns}
          handleFieldRename={handleFieldRename}
          handleCustomFieldNameValidation={handleCustomFieldNameValidation}
        />
      )}
      <OptionalFieldMappingSection
        localConfig={localConfig}
        BuildFieldMappingOptions={BuildFieldMappingOptions}
        disableDropdowns={disableDropdowns}
        handleFieldRename={handleFieldRename}
        handleCustomFieldNameValidation={handleCustomFieldNameValidation}
      />
      {isPositionChart && (
        <BudgetMappingSection
          chartConfig={chartConfig}
          localConfig={localConfig}
          BuildFieldMappingOptions={BuildFieldMappingOptions}
          disableDropdowns={disableDropdowns}
          handleFieldRename={handleFieldRename}
          handleCustomFieldNameValidation={handleCustomFieldNameValidation}
          onUpdate={handleUpdate}
        />
      )}
      {isPositionChart && showPositionHolderFields && (
        <SectionCard
          title={appstr("Data field categories")}
          titleContent={
            <Flex className={"oc-right-justify"}>
              <Button
                dataTestId={`${MAPPING_TEST_PREFIX}-reset-default-button`}
                modifier={"secondary"}
                onClick={() =>
                  handleLoadFieldMapping({
                    advancedOptions: { resetFieldData: true, mapping: localConfig.fields }
                  })
                }
              >
                {"Reset to default"}
              </Button>
            </Flex>
          }
        >
          <Flex className={"oc-pos-fields-container"}>
            <PositionHolderFields
              chartConfig={chartConfig}
              onUpdate={handleUpdate}
              onReset={handleLoadFieldMapping}
            />
          </Flex>
        </SectionCard>
      )}
      {/* Temporary suppress additional reports with position charts */}
      {!isPositionChart && (
        <AdditionalReportsMappingSection
          localConfig={localConfig}
          BuildFieldMappingOptions={BuildFieldMappingOptions}
          disableDropdowns={disableDropdowns}
          handleFieldRename={handleFieldRename}
          handleCustomFieldNameValidation={handleCustomFieldNameValidation}
          onUpdate={handleUpdate}
        />
      )}
      <LocalizationCard chartConfig={chartConfig} onUpdate={handleUpdate} />
    </SectionCardGroup>
  );
};
